import { GiphyFetch } from '@giphy/js-fetch-api'
import { AdDecision, AdResponse } from '../ads-endpoint'
import { publicRuntimeConfig } from '../env'

/**
 * Dynamically loads a test JSON file using static import paths for code-splitting.
 * @param adTestValue The test number (e.g., 1 for test1.json)
 * @returns Promise resolving to the JSON data
 */
const loadTestJson = (adTestValue: number) => {
    switch (adTestValue) {
        case 1:
            return import('./test1.json')
        case 2:
            return import('./test2.json')
        case 3:
            return import('./test3.json')
        case 4:
            return import('./test4.json')
        default:
            throw new Error(`No test response for value ${adTestValue}`)
    }
}
type TestAdResponse = {
    default: AdResponse & { gif_id: string; description: string }
}
/**
 * Recursively fetches gifs for a decision and its subdecisions.
 */
const fetchGifForDecision = async (decision: AdDecision & { gif_id?: string; subdecisions?: AdDecision[] }) => {
    if (decision.gif_id) {
        const testGif = await new GiphyFetch(publicRuntimeConfig.apiKey).gif(decision.gif_id as string)
        decision.gif = testGif.data
    }
    if (decision.subdecisions && Array.isArray(decision.subdecisions)) {
        await Promise.all(decision.subdecisions.map(fetchGifForDecision))
    }
}

/**
 * Loads an ad test response from a mock JSON file and populates the gif field for each decision and subdecision.
 * @param adTestValue The test number (e.g., 1 for test1.json)
 * @returns A tuple: [AdResponse?, errorMessage?]
 */
const getAdTestResponse = async (adTestValue: number): Promise<[AdResponse?, string?]> => {
    try {
        const { default: result } = (await loadTestJson(adTestValue)) as unknown as TestAdResponse
        console.log(`ad test ${result.description}`)
        const decisions = Object.values(result?.decisions || {}).filter(
            (d): d is AdDecision => d !== null && d !== undefined
        )
        const decisionPromises = decisions.map(fetchGifForDecision)
        await Promise.all(decisionPromises)
        return [result as AdResponse]
    } catch (e) {
        console.error(`Failed to load test${adTestValue}.json from ad-mocks`, e)
        return [, `Failed to load test${adTestValue}.json: ${e}`]
    }
}

export default getAdTestResponse
